import React from "react";
import { usePriceModeContext } from "../../hooks/usePriceMode";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import useOutsideClick from "../../hooks/useOutsideClick";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevron_down.svg";
import { ReactComponent as Checkmark } from "../../assets/icons/checkmark2.svg";
import { ReactComponent as Plus } from "../../assets/icons/plus.svg";
import { ReactComponent as Minus } from "../../assets/icons/minus.svg";

type customer = "PRIVATE" | "BUSINESS";

const options: {
  [k: string]: {
    label: MessageDescriptor;
    value: customer;
  };
} = {
  PRIVATE: {
    label: {
      id: "components.VatCustomerPicker.withVat",
      defaultMessage: "with VAT",
    },
    value: "PRIVATE",
  },
  BUSINESS: {
    label: {
      id: "components.VatCustomerPicker.withoutVat",
      defaultMessage: "without VAT",
    },
    value: "BUSINESS",
  },
};

const DropdownButton: React.FC<{
  updateCustomerType: (c: customer) => void;
  customerType: customer;
  active: boolean;
}> = ({ customerType, active, updateCustomerType }) => {
  return (
    <button
      className={`${active ? "font-bold text-gray-700" : ""} flex flex-row items-center`}
      onClick={() => updateCustomerType(customerType)}
    >
      <div className="min-w-5">{active && <Checkmark className="opacity-80 scale-90" />}</div>
      <FormattedMessage {...options[customerType].label} />
    </button>
  );
};

const VatCustomerPicker: React.FC<{ additionalCls?: string }> = ({ additionalCls }) => {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const { provided } = usePriceModeContext();
  const { customerType, updateCustomerType } = provided;

  const updateAndClose = (c: customer) => {
    updateCustomerType(c);
    setDropdownOpen(false);
  };

  const menuTogglerRef = React.useRef(null);
  useOutsideClick(menuTogglerRef, () => setDropdownOpen(false));
  return (
    <div
      className={`w-fit relative flex flex-row gap-x-2 text-gray-700 items-center ${additionalCls}`}
      ref={menuTogglerRef}
    >
      {customerType === "BUSINESS" ? (
        <Minus width={18} className="opacity-70 text-gray-500" />
      ) : (
        <Plus width={18} className="opacity-70 text-gray-500" />
      )}
      <div className="flex flex-row gap-x-1">
        <FormattedMessage
          tagName={"span"}
          id="components.VatCustomerPicker.showPrices"
          defaultMessage={"Show prices"}
        />
        <button
          className={"flex flex-row items-center gap-x-0.5 font-semibold"}
          onClick={() => setDropdownOpen(true)}
        >
          <span>
            <FormattedMessage {...options[customerType].label} />
          </span>
          <ChevronDown className="opacity-80 scale-90" />
        </button>
      </div>

      {dropdownOpen && (
        <div className="before:content-[''] before:w-2 before:h-2 before:rotate-45 before:absolute before:bg-white before:z-10 before:-top-1 before:left-1/3 before:border-l before:border-t before:border-gray-200 absolute top-6 -right-1/2 min-w-40 bg-white gap-y-3 border-gray-200 shadow-md border rounded-lg z-10 px-3 py-3 flex flex-col">
          <DropdownButton
            customerType="PRIVATE"
            updateCustomerType={updateAndClose}
            active={customerType === "PRIVATE"}
          />
          <DropdownButton
            customerType="BUSINESS"
            updateCustomerType={updateAndClose}
            active={customerType === "BUSINESS"}
          />
        </div>
      )}
    </div>
  );
};

export default VatCustomerPicker;
